import { Box, useTheme, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import _ from 'lodash';
import IFundOperationsCashFlow from '../../../interfaces/fund/operations/IFundOperationsCashFlow';
import { useTranslation } from 'react-i18next';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { useAppSettings } from '../../../../../app/features/settings/providers/AppSettingsProvider';
import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import investmentFundOperationsLang from '../../../langs/investmentFundOperationsLang';
import AppLoaderGraph from '../../../../../app/features/loader/graph/AppLoaderGraph';
import useAppFormatAmount from '../../../../../app/features/format/hooks/useAppFormatAmount';
import IFund from '../../../interfaces/fund/IFund';

type CFundCashFlowGraphProps = {
	cashFlows: IFundOperationsCashFlow[];
	fund: IFund;
};
const CFundCashFlowGraph = (props: CFundCashFlowGraphProps) => {
	const { cashFlows, fund } = props;

	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const [data, setData] = useState<
		{
			quarter: string;
			cumulatedCashInflows: number;
			cumulatedCashOutflows: number;
			callableCapital: number;
			cumulatedCashFlows: number;
		}[]
	>([]);

	useEffect(() => {
		if (cashFlows) {
			setData(
				cashFlows.map((x) => ({
					quarter: x.quarter,
					cumulatedCashInflows: displayRounding(x.cumulatedCashInflows, rounding),
					cumulatedCashOutflows: displayRounding(x.cumulatedCashOutflows, rounding) * -1,
					callableCapital: displayRounding(x.callableCapital, rounding) * -1,
					cumulatedCashFlows: displayRounding(x.cumulatedCashFlows, rounding),
				})),
			);
		}
	}, [cashFlows, displayRounding, rounding]);

	const isOpenEndedFund = fund ? fund.isOpenEndedFund : false;

	useEffect(() => {
		if (cashFlows && fund && fund.currency) {
			const root = am5.Root.new('cashFlows');

			if (i18n.language === 'fr') {
				root.locale = am5locales_fr_FR;
			} else {
				root.locale = am5locales_en_US;
			}

			const chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					layout: root.verticalLayout,
				}),
			);

			const lineXAxisColor = am5.Theme.new(root);
			lineXAxisColor.rule('Grid', ['base']).setAll({
				strokeOpacity: 1,
				strokeWidth: 2,
				stroke: am5.color(darkModeEnabled ? '#fff' : '#1a2b48'),
			});

			if (darkModeEnabled) {
				root.setThemes([am5themes_Dark.new(root), am5themes_Responsive.new(root), lineXAxisColor]);
			} else {
				root.setThemes([am5themes_Animated.new(root), am5themes_Responsive.new(root), lineXAxisColor]);
			}

			const xAxis = chart.xAxes.push(
				am5xy.CategoryAxis.new(root, {
					categoryField: 'quarter',
					renderer: am5xy.AxisRendererX.new(root, {}),
				}),
			);

			xAxis.data.setAll(data);

			const yAxis = chart.yAxes.push(
				am5xy.ValueAxis.new(root, {
					min: _.min(data.map((x) => x.cumulatedCashOutflows + x.callableCapital + x.cumulatedCashFlows)),
					extraMax: 0.1,
					renderer: am5xy.AxisRendererY.new(root, {}),
				}),
			);

			const yRenderer = yAxis.get('renderer');
			yRenderer.labels.template.setAll({
				fill: am5.color(darkModeEnabled ? '#ccc' : '#AAA'),
				fontSize: '10px',
			});

			const xRenderer = xAxis.get('renderer');
			xRenderer.labels.template.setAll({
				centerY: am5.p50,
				paddingTop: 10,
				paddingLeft: 80,
				paddingBottom: 20,
				paddingRight: 15,
				textAlign: 'center',
				fontSize: '13px',
				fontFamily: "'Poppins', sans-serif",
				fontWeight: '500',
			});

			xRenderer.grid.template.setAll({
				visible: false,
			});

			const callableCapitalTooltip = isOpenEndedFund
				? ``
				: `[width:300px]${t(investmentFundOperationsLang.graph_legend_dry_powder.tag)}[/][${
						darkModeEnabled ? '#1A2B48' : '#FFF'
				  }][bold]{callableCapital}${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
						fund.currency,
				  )}[/][/][/]\n\n`;

			const tooltip = am5.Tooltip.new(root, {
				// Disable amCharts' default background and text color so we can fully
				// control the look via the explicit settings below.
				getFillFromSprite: false,
				autoTextColor: false,
				pointerOrientation: 'left',
				labelText: `[bold]{quarter}[/]\n\n[width:300px]${t(
					investmentFundOperationsLang.graph_legend_cumulated_cash_inflows.tag,
				)}[/][#2ABDBB][bold]{cumulatedCashInflows}${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
					fund.currency,
				)}[/][/][/]\n\n[width:300px]${t(
					investmentFundOperationsLang.graph_legend_cumulated_cash_outflows.tag,
				)}[/][#86D9D8][bold]{cumulatedCashOutflows}${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
					fund.currency,
				)}[/][/][/]\n\n${callableCapitalTooltip}[width:300px]${t(
					investmentFundOperationsLang.graph_legend_cumulated_cashflows.tag,
				)}[/][#B75AFF][bold]{cumulatedCashFlows}${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
					fund.currency,
				)}[/][/][/]`,
			});

			tooltip?.get?.('background')?.setAll({
				fill: am5.color(darkModeEnabled ? '#fff' : '#1A2B48'),
				stroke: am5.color(darkModeEnabled ? '#fff' : '#1A2B48'),
				fillOpacity: 1,
			});

			const series1 = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					name: t(investmentFundOperationsLang.graph_legend_cumulated_cash_inflows.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					fill: am5.color(0x2abdbb),
					valueYField: 'cumulatedCashInflows',
					categoryXField: 'quarter',
				}),
			);

			series1.columns.template.setAll({
				tooltipY: am5.percent(10),
				templateField: 'columnSettings',
				cornerRadiusTL: 2,
				cornerRadiusTR: 2,
			});

			series1.data.setAll(data);

			const series2 = chart.series.push(
				am5xy.ColumnSeries.new(root, {
					name: t(investmentFundOperationsLang.graph_legend_cumulated_cash_outflows.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					tooltip: tooltip,
					stacked: true,
					fill: am5.color(0x86d9d8),
					valueYField: 'cumulatedCashOutflows',
					categoryXField: 'quarter',
				}),
			);

			series2.columns.template.setAll({
				tooltipY: am5.percent(10),
				templateField: 'columnSettings',
				cornerRadiusTL: 2,
				cornerRadiusTR: 2,
			});

			series2.data.setAll(data);
			if (!isOpenEndedFund) {
				const series3 = chart.series.push(
					am5xy.ColumnSeries.new(root, {
						name: t(investmentFundOperationsLang.graph_legend_dry_powder.tag),
						xAxis: xAxis,
						yAxis: yAxis,
						stacked: true,
						fill: am5.color(0x2c6172),
						valueYField: 'callableCapital',
						categoryXField: 'quarter',
					}),
				);

				series3.columns.template.setAll({
					tooltipY: am5.percent(10),
					templateField: 'columnSettings',
					cornerRadiusTL: 2,
					cornerRadiusTR: 2,
				});

				series3.data.setAll(data);

				series3.columns.template.setAll({
					width: am5.percent(50),
				});
				series3.appear();
			}

			const series4 = chart.series.push(
				am5xy.LineSeries.new(root, {
					name: t(investmentFundOperationsLang.graph_legend_cumulated_cashflows.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					fill: am5.color(0xb75aff),
					stroke: am5.color(0xb75aff),
					valueYField: 'cumulatedCashFlows',
					categoryXField: 'quarter',
				}),
			);

			series4.data.setAll(data);

			series1.columns.template.setAll({
				width: am5.percent(50),
			});
			series2.columns.template.setAll({
				width: am5.percent(50),
				tooltipX: am5.percent(0),
			});

			series4.strokes.template.setAll({
				strokeWidth: 3,
				templateField: 'strokeSettings',
			});

			chart.set('cursor', am5xy.XYCursor.new(root, {}));
			const cursor = chart.get('cursor');
			cursor?.lineY.setAll({
				visible: false,
			});
			cursor?.lineX.setAll({
				visible: false,
			});

			chart.set(
				'scrollbarX',
				am5.Scrollbar.new(root, {
					orientation: 'horizontal',
				}),
			);

			const scrollbarX = chart.get('scrollbarX');
			scrollbarX?.get?.('background')?.setAll({
				opacity: 0,
			});

			scrollbarX?.thumb.setAll({
				fill: am5.color('#ccc'),
				height: 2,
			});
			scrollbarX?.startGrip.setAll({
				width: 15,
				height: 15,
				paddingLeft: 200,
				dx: -10,
				maskContent: true,
			});
			const startGripBg = scrollbarX?.startGrip.get('background');
			startGripBg?.setAll({
				fill: am5.color('#ccc'),
			});
			scrollbarX?.endGrip.setAll({
				width: 15,
				height: 15,
				paddingLeft: 200,
				maskContent: true,
				dx: 10,
			});
			const endGripBg = scrollbarX?.endGrip.get('background');
			endGripBg?.setAll({
				fill: am5.color('#ccc'),
			});

			let legend: am5.Legend;
			let legendRoot: am5.Root;
			if (isMobile) {
				legendRoot = am5.Root.new('cashFlows-legend');
				legend = legendRoot.container.children.push(
					am5.Legend.new(legendRoot, {
						centerX: am5.percent(50),
						x: am5.percent(50),
						layout: legendRoot.verticalLayout,
						useDefaultMarker: true,
						height: am5.percent(100),
						verticalScrollbar: am5.Scrollbar.new(legendRoot, {
							orientation: 'vertical',
						}),
					}),
				);

				legend.markerRectangles.template.setAll({
					cornerRadiusTL: 10,
					cornerRadiusTR: 10,
					cornerRadiusBL: 10,
					cornerRadiusBR: 10,
				});
				legend.labels.template.setAll({
					fontSize: 14,
					fontWeight: '300',
					fill: am5.color(theme.palette.primary.main),
				});
				legend.valueLabels.template.setAll({
					fontSize: 14,
					fontWeight: '400',
					fill: am5.color(theme.palette.primary.main),
				});
			} else {
				legend = chart.children.push(
					am5.Legend.new(root, {
						centerX: am5.p50,
						centerY: am5.p50,
						x: am5.p50,
						y: am5.p100,
						// fontFamily: "'Poppins', sans-serif",
					}),
				);
			}

			legend.data.setAll(chart.series.values);

			chart.appear(1000, 100);
			series1.appear();
			series2.appear();
			series4.appear();

			return () => {
				root.dispose();
				isMobile ? legendRoot.dispose() : null;
			};
		}
	}, [
		cashFlows,
		data,
		t,
		darkModeEnabled,
		rounding,
		isMobile,
		i18n.language,
		theme.palette.primary.main,
		displayRoundingSuffix,
		displayCurrencySymbol,
		fund,
		isOpenEndedFund,
	]);

	return (
		<>
			{cashFlows ? (
				<>
					<Box id="cashFlows" sx={{ height: '400px', width: '100%' }} />
					<Box
						id="cashFlows-legend"
						sx={{ height: '130px', display: { xs: 'block', sm: 'none' }, marginBottom: '20px' }}
					/>
				</>
			) : (
				<AppLoaderGraph height={400} />
			)}
		</>
	);
};

export default CFundCashFlowGraph;
